/**
 * Wiring by example (S11/C5) — when a cross-filter field goes active and
 * sibling tiles COULD honor it but aren't bound yet, offer to wire them
 * once: candidate columns come from each tile's last result set (exact /
 * `_field` / `field_` name match). Confirm appends a FilterBinding to the
 * tile; skip records the (dashboard, field) so we never nag this session.
 * Never a mapping matrix upfront.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { getEngineForConnection } from '../../../../connections/utils/connectionLookup';
import type { ActiveDashboardFilter } from '../../types/dashboardRuntime';
import { isQueryBackedTile, type Dashboard, type FilterBinding } from '../../types/dashboardTypes';
import { bindingForField, canTileComposeFilters } from '../../services/crossFilter';
import type { QueryBackedTile } from '../../services/tileExecutionService';
import { useDashboardStore } from '../../store/dashboardStore';
import { useDashboardRuntimeStore, tileRuntimeKey } from '../../store/dashboardRuntimeStore';
import {
  selectActiveFilters,
  useDashboardCrossFilterStore,
} from '../../store/dashboardCrossFilterStore';

type CandidateColumn = { name: string; dataType?: string };

type Candidate = {
  tile: QueryBackedTile;
  columns: CandidateColumn[];
  /** Bound-able but the predicate has nowhere to go (no slot, not injectable). */
  needsSlot: boolean;
};

/**
 * Recharts keeps a tooltip up until the chart's own wrapper sees the pointer
 * leave. A modal opening straight from a mark click means that mouseout never
 * arrives (Radix parks `pointer-events: none` on the body), so the tooltip
 * freezes ON TOP of the overlay (S24 review). Nudge every mounted chart with
 * the DOM event React derives `onMouseLeave` from — relatedTarget outside the
 * wrapper — which is what Recharts listens on. Best-effort and idempotent:
 * charts that already dismissed theirs simply re-settle.
 */
function dismissChartTooltips(): void {
  for (const el of document.querySelectorAll('.recharts-wrapper')) {
    el.dispatchEvent(new MouseEvent('mouseout', { bubbles: true, relatedTarget: document.body }));
  }
}

const columnValueType = (
  dataType: string | undefined,
  fallback: FilterBinding['valueType'],
): FilterBinding['valueType'] => {
  if (!dataType) return fallback;
  const t = dataType.toLowerCase();
  if (/bit|bool/.test(t)) return 'boolean';
  if (/date|time/.test(t)) return 'date';
  if (/int|num|dec|float|real|money|double/.test(t)) return 'number';
  return 'string';
};

function candidatesFor(dashboard: Dashboard, filter: ActiveDashboardFilter): Candidate[] {
  const field = filter.dashboardField.toLowerCase();
  const byTile = useDashboardRuntimeStore.getState().byTile;
  const out: Candidate[] = [];
  for (const tile of dashboard.tiles) {
    if (!isQueryBackedTile(tile)) continue;
    if (tile.id === filter.sourceTileId || bindingForField(tile, filter.dashboardField)) continue;
    const columns =
      byTile[tileRuntimeKey(dashboard.id, tile.id)]?.lastSuccess?.result.selected.columns;
    if (!columns) continue;
    const matches = columns.filter((c) => {
      const n = c.name.toLowerCase();
      return n === field || n.endsWith(`_${field}`) || n.startsWith(`${field}_`);
    });
    if (matches.length === 0) continue;
    out.push({
      tile,
      columns: matches.map((c) => ({ name: c.name, dataType: c.dataType })),
      needsSlot: !canTileComposeFilters(tile, getEngineForConnection(tile.connectionId)),
    });
  }
  return out;
}

export function FilterWiringPrompt({ dashboard }: { dashboard: Dashboard }) {
  const filters = useDashboardCrossFilterStore(selectActiveFilters(dashboard.id));
  const promptedList = useDashboardCrossFilterStore((s) => s.promptedFields[dashboard.id]);
  const markPrompted = useDashboardCrossFilterStore((s) => s.markPrompted);
  // Candidates read result columns — recompute when tile runs settle.
  const byTile = useDashboardRuntimeStore((s) => s.byTile);
  const updateTile = useDashboardStore((s) => s.updateTile);
  const { track } = useAnalytics();

  const pending = useMemo(() => {
    const filter = filters.find(
      (f) => !(promptedList ?? []).includes(f.dashboardField.toLowerCase()),
    );
    if (!filter) return null;
    const candidates = candidatesFor(dashboard, filter);
    return candidates.length > 0 ? { filter, candidates } : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- byTile drives candidatesFor
  }, [filters, promptedList, dashboard, byTile]);

  const [picks, setPicks] = useState<Record<string, { checked: boolean; column: string }>>({});
  const pendingField = pending?.filter.dashboardField;
  useEffect(() => {
    if (!pending) return;
    dismissChartTooltips();
    setPicks(
      Object.fromEntries(
        pending.candidates.map((c) => [
          c.tile.id,
          { checked: !c.needsSlot, column: c.columns[0].name },
        ]),
      ),
    );
    // Re-seed per prompted field, not per candidate-list identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingField]);

  if (!pending) return null;
  const { filter, candidates } = pending;
  const blockedCount = candidates.filter((c) => c.needsSlot).length;

  const skip = () => markPrompted(dashboard.id, filter.dashboardField);

  const confirm = async () => {
    const chosen = candidates.filter((c) => !c.needsSlot && picks[c.tile.id]?.checked);
    for (const c of chosen) {
      // The CHOSEN column's dataType governs the bind type (C3 finding 5) —
      // e.g. picking `channel_name` over `channel_id` must not keep 'number'.
      const picked = c.columns.find((col) => col.name === picks[c.tile.id].column);
      const binding: FilterBinding = {
        dashboardField: filter.dashboardField,
        targetExpression: picks[c.tile.id].column,
        valueType: columnValueType(picked?.dataType, filter.valueType),
      };
      await updateTile(dashboard.id, {
        ...c.tile,
        filterBindings: [...(c.tile.filterBindings ?? []), binding],
      });
    }
    markPrompted(dashboard.id, filter.dashboardField);
    if (chosen.length > 0) track('feature:dashboard_crossfilter_wire', { count: chosen.length });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && skip()}>
      <DialogContent className="max-w-md" data-testid="dashboard-wiring-prompt">
        <DialogHeader>
          <DialogTitle>Filter other tiles by {filter.dashboardField}?</DialogTitle>
          <DialogDescription>
            These tiles have a matching column. Wiring is saved on the tile — future clicks filter
            them instantly.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          {candidates.map((c) => (
            <div key={c.tile.id} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={picks[c.tile.id]?.checked ?? false}
                disabled={c.needsSlot}
                onCheckedChange={(checked) =>
                  setPicks((p) => ({ ...p, [c.tile.id]: { ...p[c.tile.id], checked: checked === true } }))
                }
                data-testid={`wiring-tile-${c.tile.id}`}
              />
              <span className="min-w-0 flex-1 truncate" title={c.tile.title}>
                {c.tile.title}
              </span>
              {c.needsSlot ? (
                <span className="text-xs text-muted-foreground">
                  add {'{{dashboard_filters}}'} to its SQL first
                </span>
              ) : c.columns.length > 1 ? (
                <Select
                  value={picks[c.tile.id]?.column}
                  onValueChange={(column) =>
                    setPicks((p) => ({ ...p, [c.tile.id]: { ...p[c.tile.id], column } }))
                  }
                >
                  <SelectTrigger className="h-7 w-40" data-testid={`wiring-column-${c.tile.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {c.columns.map((col) => (
                      <SelectItem key={col.name} value={col.name}>
                        {col.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <code className="text-xs text-muted-foreground">{c.columns[0].name}</code>
              )}
            </div>
          ))}
          {/* Expectation-setting: the disabled rows explain THEMSELVES, but
              not how much of the dashboard stays unfiltered (S24 review). */}
          {blockedCount > 0 && (
            <p className="text-xs text-muted-foreground" data-testid="wiring-blocked-note">
              {blockedCount === 1 ? "1 tile can't participate" : `${blockedCount} tiles can't participate`} —
              add {'{{dashboard_filters}}'} to {blockedCount === 1 ? 'its' : 'their'} SQL to include{' '}
              {blockedCount === 1 ? 'it' : 'them'}.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={skip} data-testid="wiring-skip">
            Not now
          </Button>
          <Button onClick={() => void confirm()} data-testid="wiring-confirm">
            Wire filters
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
